// components/HomePage/Hero.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ChevronDown } from 'lucide-react';
import {
  SERVICES,
  SERVICE_CATEGORIES,
  technologyIndex,
} from '../../data/serviceCatalog';
import Reveal from '../common/Reveal';

/**
 * The four rotating words are the owner's original tagline rotation and
 * live at module scope so the typing effect's dependency array is stable
 * (the old version re-created this array on every render, which both
 * tripped react-hooks/exhaustive-deps and rebuilt the timer ~10x/second).
 */
const PHRASES = [
  'Digital Transformation',
  'Business Growth',
  'Innovation',
  'Success',
];

/** Longest phrase, used to reserve the headline's line box. Derived, not typed twice. */
const LONGEST_PHRASE = PHRASES.reduce(
  (longest, phrase) => (phrase.length > longest.length ? phrase : longest),
  ''
);

const TYPE_MS = 78;
const DELETE_MS = 36;
const HOLD_MS = 1700;
const GAP_MS = 420;
const START_MS = 500;

/** Media assets. The poster doubles as the static hero background. */
const POSTER_SRC = '/BG/hero-new-9.jpg';
const POSTER_W = 1920;
const POSTER_H = 1825;
const VIDEO_SRC = '/BG/background.mp4';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event) => setReduced(event.matches);
    setReduced(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

const Hero = () => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [typed, setTyped] = useState(PHRASES[0]);
  const [showVideo, setShowVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);

  // Real data, computed once: the catalog is the only evidence base.
  const topTechnologies = useMemo(
    () => technologyIndex().slice(0, 6).map((entry) => entry.name),
    []
  );

  /**
   * Typing effect: one timer, always cleared. Under prefers-reduced-motion
   * the first phrase is shown statically and no timer is ever started.
   */
  useEffect(() => {
    if (prefersReducedMotion) {
      setTyped(PHRASES[0]);
      return undefined;
    }

    let phraseIndex = 0;
    let charCount = 0;
    let deleting = false;
    let timer;

    const tick = () => {
      // Don't advance the animation while the tab is in the background.
      if (typeof document !== 'undefined' && document.hidden) {
        timer = setTimeout(tick, 800);
        return;
      }

      const full = PHRASES[phraseIndex];

      if (deleting) {
        charCount -= 1;
        setTyped(full.slice(0, charCount));
        if (charCount === 0) {
          deleting = false;
          phraseIndex = (phraseIndex + 1) % PHRASES.length;
          timer = setTimeout(tick, GAP_MS);
          return;
        }
        timer = setTimeout(tick, DELETE_MS);
        return;
      }

      charCount += 1;
      setTyped(full.slice(0, charCount));
      if (charCount === full.length) {
        deleting = true;
        timer = setTimeout(tick, HOLD_MS);
        return;
      }
      timer = setTimeout(tick, TYPE_MS);
    };

    setTyped('');
    timer = setTimeout(tick, START_MS);
    return () => clearTimeout(timer);
  }, [prefersReducedMotion]);

  /**
   * Decide whether the background video is allowed to exist at all.
   *
   * It now plays on phones as well as desktop. The only things that still
   * suppress it are a data-saver setting, a 2G connection, or a
   * reduced-motion preference, all of which are explicit signals from the
   * visitor that they do not want a heavy autoplaying background.
   *
   * Loading is still deferred until after the window load event so the
   * video never competes with the poster image for the largest
   * contentful paint.
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let cancelled = false;
    let delayId;

    const evaluate = () => {
      if (cancelled) return;
      const connection =
        navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const frugal = Boolean(
        connection &&
          (connection.saveData || /(^|-)(2g|slow-2g)$/.test(connection.effectiveType || ''))
      );
      setShowVideo(!prefersReducedMotion && !frugal);
    };

    const start = () => {
      delayId = window.setTimeout(evaluate, 400);
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }

    return () => {
      cancelled = true;
      window.clearTimeout(delayId);
      window.removeEventListener('load', start);
    };
  }, [prefersReducedMotion]);

  /** Stop decoding video frames once the hero has scrolled away. */
  useEffect(() => {
    const video = videoRef.current;
    if (!showVideo || !video || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const played = video.play();
          if (played && typeof played.catch === 'function') played.catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.01 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [showVideo]);

  return (
    <section
      className="relative isolate flex min-h-screen items-center bg-brand-950 supports-[min-height:100svh]:min-h-[100svh]"
      aria-label="DepthLogix introduction"
    >
      {/* --- Background stack. Clipping lives here, never on the section,
              so content can never be cut off on a short viewport. --- */}
      <div className="absolute inset-0 overflow-hidden">
        {/*
          The LCP element. 1920x1825, 105 KB — inside the ~200 KB eager-image
          budget in DESIGN.md §8. It also serves as the video poster and as
          the static treatment on phones / under reduced motion.
        */}
        <img
          src={POSTER_SRC}
          alt=""
          aria-hidden="true"
          width={POSTER_W}
          height={POSTER_H}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />

        {showVideo && (
          <video
            ref={videoRef}
            className="absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ease-out-expo"
            style={{ opacity: videoReady ? 1 : 0 }}
            src={VIDEO_SRC}
            poster={POSTER_SRC}
            preload="none"
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            tabIndex={-1}
            onCanPlay={() => setVideoReady(true)}
          />
        )}

        {/* Vertical scrim: darkens only the two bands that need it, under
            the navbar and along the bottom rule. Clear through the middle
            so the footage actually reads. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-brand-950/75 via-transparent to-brand-950/85"
        />
        {/* Horizontal scrim: weighted to the left, behind the text column.
            On large screens it clears completely on the right so the video
            is fully visible there. Legibility over the remaining footage
            comes from the text shadow on the headline and lead. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-r from-brand-950/85 via-brand-950/70 to-brand-950/60 lg:from-brand-950/88 lg:via-brand-950/50 lg:to-transparent"
        />
        {/* Faint engineering grid from the design system. */}
        <div aria-hidden="true" className="grid-field-dark absolute inset-0 opacity-40" />
      </div>

      {/* --- Content --- */}
      <div className="container-custom relative z-10 w-full pb-16 pt-28 sm:pt-32 lg:pb-20 lg:pt-36">
        <div className="grid items-start gap-x-14 gap-y-12 lg:grid-cols-[1.25fr_0.75fr] lg:items-end">
          <div>
            <p className="hero-legible flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[11px] uppercase tracking-eyebrow text-brand-200">
              <span aria-hidden="true" className="h-px w-6 bg-brand-400/60" />
              {SERVICES.length} services
              <span aria-hidden="true" className="text-brand-300/50">
                /
              </span>
              {SERVICE_CATEGORIES.length} practice areas
            </p>

            {/* break-words guards the 320px case, where a single long word
                in the rotation is wider than the viewport. */}
            <h1 className="hero-legible mt-6 break-words font-bricolage text-display-lg font-bold text-white">
              <span className="block text-balance">Where Technology Meets</span>
              {/* The rotating half is announced once, statically, so assistive
                  tech and crawlers get a stable headline. */}
              <span className="sr-only">{PHRASES[0]}</span>
              <span aria-hidden="true" className="relative mt-1 block">
                {/* Invisible longest phrase reserves the exact line box at
                    every width — the headline never reflows while typing. */}
                <span className="invisible select-none">{LONGEST_PHRASE}</span>
                <span className="absolute inset-0 text-brand-300">
                  {typed}
                  {!prefersReducedMotion && (
                    <span className="animate-caret ml-1.5 inline-block h-[0.78em] w-[3px] translate-y-[0.02em] rounded-sm bg-brand-400 align-baseline" />
                  )}
                </span>
              </span>
            </h1>

            {/* Tighter on small screens: only "Digital Transformation" wraps
                to two lines at that width, so the reserved line box leaves a
                gap under the shorter phrases. Less top margin absorbs it. */}
            <p className="hero-legible mt-4 max-w-[58ch] text-pretty text-base leading-relaxed text-ink-100 sm:mt-7 sm:text-lg">
              We build custom software: web and mobile applications, ERP and CRM
              platforms, API integrations and AI systems that fit the workflows
              you already run.
            </p>

            <div className="mt-9 flex flex-wrap gap-3 sm:gap-4">
              {/* The two shared button styles from DESIGN.md §5 — the hero is
                  one of the seven places the blue-to-blue primary CTA has
                  always appeared, so it is not restyled locally. */}
              <Link to="/contact" className="btn-primary group">
                Start Your Project
                <ArrowRight
                  aria-hidden="true"
                  className="h-4 w-4 transition-transform duration-200 ease-out-expo group-hover:translate-x-1"
                />
              </Link>

              <Link to="/portfolio" className="btn-secondary group">
                View Our Work
                <ArrowUpRight
                  aria-hidden="true"
                  className="h-4 w-4 transition-transform duration-200 ease-out-expo group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </Link>
            </div>
          </div>

          {/* Quiet technical panel — the four real practice areas from the
              catalog. backdrop-blur is here for legibility over moving
              video, not as decoration. */}
          <Reveal
            className="rounded-panel border border-white/10 bg-brand-950/90 p-5 backdrop-blur-md sm:p-6"
            delay={120}
          >
            <div className="flex items-baseline justify-between gap-4 font-mono text-[11px] uppercase tracking-eyebrow">
              <span className="text-brand-200">What we build</span>
              <span className="text-ink-300">{SERVICES.length} services</span>
            </div>

            <ul className="mt-4 grid grid-cols-1 gap-x-6 sm:grid-cols-2 lg:grid-cols-1">
              {SERVICE_CATEGORIES.map((category, index) => (
                <li
                  key={category.key}
                  className="flex items-baseline gap-3 border-t border-white/10 py-3"
                >
                  <span className="font-mono text-[11px] text-brand-300/80">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="min-w-0 break-words font-bricolage text-sm font-semibold leading-snug text-white sm:text-[15px]">
                    {category.label}
                  </span>
                </li>
              ))}
            </ul>

            <Link
              to="/services"
              className="group mt-4 inline-flex items-center gap-2 border-t border-white/10 pt-4 font-mono text-[11px] uppercase tracking-eyebrow text-brand-200 transition-colors duration-200 hover:text-white"
            >
              Browse all services
              <ArrowRight
                aria-hidden="true"
                className="h-3.5 w-3.5 transition-transform duration-200 ease-out-expo group-hover:translate-x-1"
              />
            </Link>
          </Reveal>
        </div>

        {/* Bottom rule: real technologies from the catalog + scroll cue. */}
        {/* Under reduced motion the scroll cue is gone, so on small screens
            (where the technology line is hidden too) the rule would be empty
            — drop the whole strip rather than leave a stray hairline. */}
        <Reveal
          className={`mt-12 items-center justify-between gap-6 border-t border-white/10 pt-5 lg:mt-16 ${
            prefersReducedMotion ? 'hidden md:flex' : 'flex'
          }`}
          delay={200}
        >
          <p className="hidden flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-ink-300 md:flex">
            <span className="text-brand-200">Technologies</span>
            {topTechnologies.map((name) => (
              <span key={name} className="flex items-center gap-3">
                <span aria-hidden="true" className="text-white/20">
                  ·
                </span>
                {name}
              </span>
            ))}
          </p>

          {!prefersReducedMotion && (
            <span
              aria-hidden="true"
              className="ml-auto flex items-center gap-2 font-mono text-[11px] uppercase tracking-eyebrow text-ink-300"
            >
              Scroll
              <ChevronDown className="h-3.5 w-3.5 animate-bounce" />
            </span>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default Hero;
